import { formatRestrictedSqlValue } from "./customReportMeasures.js";

export const DISPLAY_VALUES = "values";
export const DISPLAY_PERCENTAGE = "percentage";
export const PERCENTAGE_OF_ROW = "row";
export const PERCENTAGE_OF_COLUMN = "column";

export const getMeasureId = (column) =>
  String(column?.measure_id ?? column?.measureId ?? column?.key ?? "");

export const isPresentNumber = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

export const mergeMeasureColumnMetadata = (data, measureColumns, configuredMeasures = []) => {
  const configuredById = new Map(
    configuredMeasures.map((measure) => [String(measure.id), measure])
  );
  const responseMeasureColumns = Array.isArray(data?.measureColumns)
    ? data.measureColumns
    : Array.isArray(data?.measure_columns)
      ? data.measure_columns
      : [];
  const responseById = new Map(
    responseMeasureColumns.map((column) => [getMeasureId(column), column])
  );
  return measureColumns.map((column) => {
    const id = getMeasureId(column);
    const configured = configuredById.get(id) || {};
    const response = responseById.get(id) || {};
    const merged = { ...configured, ...column, ...response };
    const format = String(merged.format || configured.format || "number").toLowerCase();
    return {
      ...merged,
      key: column.key,
      measureId: id,
      format,
      percentageEligible:
        format === "percentage" ? false : merged.percentageEligible === true,
    };
  });
};

export const hasPercentageEligibleMeasure = (measureColumns) =>
  measureColumns.some((column) => column?.percentageEligible === true);

const hasLegacyAggregationParameter = (parameter) =>
  /[a-z0-9_]/i.test(String(parameter ?? "").trim());

export const getLegacyStandardAggregationSemantics = (visualization = {}) => {
  const requestedOperator = String(
    visualization?.aggregationOperator ?? visualization?.aggregation_operator ?? "count"
  )
    .trim()
    .toLowerCase();
  const hasParameter = hasLegacyAggregationParameter(
    visualization?.aggregationParameter ?? visualization?.aggregation_parameter
  );
  const supportedOperators = new Set(["sum", "avg", "min", "max"]);
  const effectiveOperator =
    requestedOperator === "count"
      ? "count"
      : !hasParameter || !supportedOperators.has(requestedOperator)
        ? "count"
        : requestedOperator;
  return {
    requestedOperator,
    effectiveOperator,
    hasParameter,
    rowDerivedTotalEligible: effectiveOperator === "count" || effectiveOperator === "sum",
    percentageEligible: effectiveOperator === "count" || effectiveOperator === "sum",
  };
};

const getValueFromMap = (values, column) => {
  if (!values || typeof values !== "object" || Array.isArray(values)) return undefined;
  const id = getMeasureId(column);
  if (Object.prototype.hasOwnProperty.call(values, id)) return values[id];
  if (Object.prototype.hasOwnProperty.call(values, column?.key)) return values[column.key];
  return undefined;
};

export const getGrandTotal = (data, column) =>
  getValueFromMap(data?.totals?.grand ?? data?.totals?.grandTotal ?? data?.totals?.grand_total, column);

const tupleEquals = (left, right) =>
  Array.isArray(left) &&
  Array.isArray(right) &&
  left.length === right.length &&
  left.every((value, index) => Object.is(value, right[index]));

export const getAxisTotal = (data, axis, tuple, column) => {
  const entries = Array.isArray(data?.totals?.[axis]) ? data.totals[axis] : [];
  const matched = entries.find((entry) => tupleEquals(entry?.key, tuple));
  return getValueFromMap(matched?.values, column);
};

export const sumRawMeasure = (rows, column) => {
  let sawValue = false;
  const sum = (Array.isArray(rows) ? rows : []).reduce((total, row) => {
    const value = row?.[column?.key];
    if (!isPresentNumber(value)) return total;
    sawValue = true;
    return total + Number(value);
  }, 0);
  return sawValue ? sum : null;
};

export const getDisplayNumericValue = ({ value, column, mode, denominator }) => {
  if (!isPresentNumber(value)) return null;
  if (mode !== DISPLAY_PERCENTAGE || column?.percentageEligible !== true) return Number(value);
  if (!isPresentNumber(denominator) || Number(denominator) === 0) return null;
  return Number(value) / Number(denominator);
};

export const formatDisplayedMeasure = ({ value, column, mode, denominator }) => {
  const displayed = getDisplayNumericValue({ value, column, mode, denominator });
  if (displayed === null) return "-";
  return formatRestrictedSqlValue(
    displayed,
    mode === DISPLAY_PERCENTAGE && column?.percentageEligible === true
      ? "percentage"
      : column?.format
  );
};

export const getDisplayModeSettings = (settings = {}, defaultPercentageOf = PERCENTAGE_OF_COLUMN) => ({
  mode: settings.mode === DISPLAY_PERCENTAGE ? DISPLAY_PERCENTAGE : DISPLAY_VALUES,
  percentageOf:
    settings.percentageOf === PERCENTAGE_OF_ROW || settings.percentageBase === PERCENTAGE_OF_ROW
      ? PERCENTAGE_OF_ROW
      : defaultPercentageOf,
});
